import type { CallbackInterface } from 'recoil';
import { atom, atomFamily, DefaultValue, selector, selectorFamily, waitForAll } from 'recoil';
import type { ComputedDateRange } from 'venn-utils';
import { assertExhaustive, assertNotNil, IS_PROD } from 'venn-utils';
import type { BlockId, StudioRequestSubject, Subject } from '../types';
import type { DateRange } from 'venn-ui-kit';
import { DEFAULT_MAX_RANGE, getRangeFromType } from 'venn-ui-kit';
import { recoilHistoryEffect } from '../../effects';
import {
  analysisSubjectQuery,
  blockSubjects,
  globalSubjects,
  requestSubjects,
  benchmarkRequestSubjects,
} from './subjects';
import { compact } from 'lodash';
import { blockBenchmarkSubject, blockBenchmarkType, globalBenchmarkSubject, globalBenchmarkType } from './benchmark';
import { unsavedChangesEffect } from '../../effects/unsavedChangesEffect';
import { isLinked } from './linked';
import { viewInputManagementVersion } from './input-management/rolloutUtils';
import type { DateRangeInputId } from './input-management/dateRangeInput';
import {
  dateRangeInputBlockComputedRange,
  blockDateRangeInputState,
  dateRangeInputDateRangeState,
  dateRangeInputsState,
  dateRangeInputNameState,
  dateRangeInputConsistencyState,
} from './input-management/dateRangeInput';
import { recoilGetSubjectGroupRange } from '../async/getSubjectGroupRange';
import { allBlockIdsState } from '../grid';
import { resetOnStudioReset } from '../../effects/signalEffects';

export const subjectsDateRanges = selectorFamily<ComputedDateRange[], Subject[]>({
  key: 'subjectsDateRanges',
  get:
    (subjects) =>
    ({ get }) => {
      return get(waitForAll(subjects.map((subject) => subjectGroupDateRangeQuery([subject]))));
    },
});

/**
 * Waits for both the asynchronous subject query AND for the asynchronous group range query.
 *
 * Handy to keep separate from {@link subjectsDateRanges} because it allows subjectsDateRanges to function with greater parallelism,
 * because it allows each async thread to progress through both stages (subject query, date query) without blocking behavior.
 */
const subjectGroupDateRangeQuery = selectorFamily<ComputedDateRange, Subject[]>({
  key: 'subjectGroupDateRange',
  get:
    (subjects) =>
    ({ get }) => {
      const apiSubjects = get(requestSubjects(subjects));
      return recoilGetSubjectGroupRange(get, apiSubjects);
    },
});

/**
 * @deprecated this state uses functionality that will no longer exist with input management V2.
 * Although features are currently allowed to use this state, the feature should also support input
 * management V2 or be disabled in input management V2.
 */
export const fullGlobalDateRange = selector<ComputedDateRange>({
  key: 'fullGlobalDateRange',
  get: ({ get }) => {
    const subjects: Subject[] = get(globalSubjects);
    const subjectRequests: StudioRequestSubject[] = get(requestSubjects(subjects));

    const benchmarkType = get(globalBenchmarkType);
    let benchmarkSubjects: Subject[] = [];
    if (benchmarkType === 'INDIVIDUAL') {
      const analysisSubjects = get(waitForAll(subjects.map((s) => analysisSubjectQuery(s))));
      benchmarkSubjects = compact(analysisSubjects.map((subject) => subject.activeBenchmark));
    } else if (benchmarkType === 'COMMON') {
      benchmarkSubjects = compact([get(globalBenchmarkSubject)]);
    }
    const benchmarkRequests: StudioRequestSubject[] = get(benchmarkRequestSubjects(benchmarkSubjects));

    return recoilGetSubjectGroupRange(get, [...subjectRequests, ...benchmarkRequests]);
  },
});

/**
 * The full block date range based on the subjects applied to the block, but not including
 * any user-defined date input or overriden date.
 *
 * In input management V2, a block may have an undefined date range when no input is attached to it.
 * However, in Input management V1 the date range should always be defined.
 */
const fullBlockDateRange = selectorFamily<ComputedDateRange | undefined, BlockId>({
  key: 'fullBlockDateRange',
  get:
    (id) =>
    ({ get }) => {
      const inputManagementVersion = get(viewInputManagementVersion);

      if (inputManagementVersion === 2) {
        if (!IS_PROD) {
          // eslint-disable-next-line no-console
          console.warn(
            'input management V2 doesn`t use or support the concept of a full block date range date range; however, get might be called as recoil proactively updates caches.',
          );
        }
        return undefined;
      }

      if (inputManagementVersion === 1) {
        const subjects: Subject[] = get(blockSubjects(id));
        const subjectRequests: StudioRequestSubject[] = get(requestSubjects(subjects));

        const benchmarkType = get(blockBenchmarkType(id));
        let benchmarkSubjects: Subject[] = [];
        if (benchmarkType === 'INDIVIDUAL') {
          const analysisSubjects = get(waitForAll(subjects.map((s) => analysisSubjectQuery(s))));
          benchmarkSubjects = compact(analysisSubjects.map((subject) => subject.activeBenchmark));
        } else if (benchmarkType === 'COMMON') {
          benchmarkSubjects = compact([get(blockBenchmarkSubject(id))]);
        }
        const benchmarkRequests: StudioRequestSubject[] = get(benchmarkRequestSubjects(benchmarkSubjects));

        return recoilGetSubjectGroupRange(get, [
          ...subjectRequests.filter((s) => !s.private),
          ...benchmarkRequests.filter((s) => !s.private),
        ]);
      }

      throw assertExhaustive(inputManagementVersion);
    },
});

/**
 * @deprecated this state uses functionality that will no longer exist with input management V2.
 * Although features are currently allowed to use this state, the feature should also support input
 * management V2 or be disabled in input management V2.
 */
export const overriddenGlobalDateRange = atom<DateRange | undefined>({
  key: 'overriddenGlobalDateRange',
  default: { period: 'full' },
  effects: [recoilHistoryEffect, unsavedChangesEffect, resetOnStudioReset],
});

export const overriddenBlockDateRange = atomFamily<DateRange | undefined, BlockId>({
  key: 'overriddenBlockDateRange',
  default: { period: 'full' },
  effects: [recoilHistoryEffect, unsavedChangesEffect],
});

/**
 * @deprecated this state uses functionality that will no longer exist with input management V2.
 * Although features are currently allowed to use this state, the feature should also support input
 * management V2 or be disabled in input management V2.
 */
export const globalDateRange = selector<ComputedDateRange>({
  key: 'globalDateRange',
  get: ({ get }) => {
    const inputManagementVersion = get(viewInputManagementVersion);
    if (inputManagementVersion === 2) {
      if (!IS_PROD) {
        // eslint-disable-next-line no-console
        console.warn(
          'input management V2 doesn`t use or support global date range; however, get might be called as recoil proactively updates caches.',
        );
      }
      return {
        range: {},
        maxRange: DEFAULT_MAX_RANGE,
        frequency: 'MONTHLY',
      };
    }
    if (inputManagementVersion === 1) {
      const fullGlobalRange = get(fullGlobalDateRange);
      const overriddenGlobalRange = get(overriddenGlobalDateRange);
      const range = getRange(overriddenGlobalRange, fullGlobalRange);

      return {
        range,
        maxRange: fullGlobalRange.maxRange,
        frequency: fullGlobalRange.frequency,
      };
    }
    throw assertExhaustive(inputManagementVersion);
  },
  set: ({ set, get }, computedDateRange) => {
    const inputManagementVersion = get(viewInputManagementVersion);
    if (inputManagementVersion === 2) {
      throw new Error('input management V2 does not support a global date range');
    } else if (inputManagementVersion === 1) {
      if (computedDateRange instanceof DefaultValue) {
        set(overriddenGlobalDateRange, computedDateRange);
      } else {
        set(overriddenGlobalDateRange, computedDateRange.range);
      }
    } else {
      throw assertExhaustive(inputManagementVersion);
    }
  },
});

/**
 * In input management V2, a block may have an undefined date range when no input is attached to it.
 * However, in Input management V1 the date range should always be defined.
 */
export const blockDateRange = selectorFamily<ComputedDateRange | undefined, BlockId>({
  key: 'blockRange',
  get:
    (id) =>
    ({ get }) => {
      const inputManagementVersion = get(viewInputManagementVersion);
      if (inputManagementVersion === 2) {
        const dateRangeInputId = get(blockDateRangeInputState(id));
        if (!dateRangeInputId) {
          return undefined;
        }

        const fullBlockRange = get(dateRangeInputBlockComputedRange({ inputId: dateRangeInputId, blockId: id }));
        const dateRangeInputRange = get(dateRangeInputDateRangeState(dateRangeInputId));

        const range = getRange(dateRangeInputRange, fullBlockRange);
        return {
          range,
          maxRange: fullBlockRange.maxRange,
          frequency: fullBlockRange.frequency,
        };
      }

      if (inputManagementVersion === 1) {
        const linked = get(isLinked(id));

        if (linked) {
          const fullGlobalRange = get(fullGlobalDateRange);
          const overriddenGlobalRange = get(overriddenGlobalDateRange);

          const range = getRange(overriddenGlobalRange, fullGlobalRange);
          return {
            range,
            maxRange: fullGlobalRange.maxRange,
            frequency: fullGlobalRange.frequency,
          };
        }

        const fullBlockRange = assertNotNil(
          get(fullBlockDateRange(id)),
          'Block date ranges must not be undefined in input management V1.',
        );
        const overriddenBlockRange = get(overriddenBlockDateRange(id));

        const range = getRange(overriddenBlockRange, fullBlockRange);
        return {
          range,
          maxRange: fullBlockRange.maxRange,
          frequency: fullBlockRange.frequency,
        };
      }

      throw assertExhaustive(inputManagementVersion);
    },
  set:
    (id) =>
    ({ get, set }, computedDateRange) => {
      const inputManagementVersion = get(viewInputManagementVersion);
      if (inputManagementVersion === 2) {
        // input management version 2 has no concept of blocks having their own date range setting/override,
        throw new Error('setting block date range in input management V2 is not supported');
      } else if (inputManagementVersion === 1) {
        const linked = get(isLinked(id));
        if (computedDateRange instanceof DefaultValue) {
          if (linked) {
            set(overriddenGlobalDateRange, computedDateRange);
          } else {
            set(overriddenBlockDateRange(id), computedDateRange);
          }
        } else if (linked) {
          set(
            overriddenGlobalDateRange,
            assertNotNil(computedDateRange, 'Block date ranges must not be undefined in input management V1.').range,
          );
        } else {
          set(
            overriddenBlockDateRange(id),
            assertNotNil(computedDateRange, 'Block date ranges must not be undefined in input management V1.').range,
          );
        }
      } else {
        throw assertExhaustive(inputManagementVersion);
      }
    },
});

const getRange = (range: DateRange | undefined, fullRange: ComputedDateRange) => {
  if (!range) {
    return fullRange.range;
  }
  // TODO: convert frequency to granuality
  const newRange = range.period ? getRangeFromType(range.period, fullRange.maxRange, 'day') : range;
  return {
    to: newRange.to,
    from: newRange.from,
    period: range.period,
  };
};

/** use this in a useRecoilCallback hook to delete date range inputs for a given date range id */
export const onDateInputDelete =
  ({ snapshot, set, reset }: CallbackInterface) =>
  async (inputId: DateRangeInputId) => {
    set(dateRangeInputsState, (current) => current.filter((id) => id !== inputId));

    reset(dateRangeInputNameState(inputId));
    reset(dateRangeInputConsistencyState(inputId));
    reset(dateRangeInputDateRangeState(inputId));

    const blockIds = await snapshot.getPromise(allBlockIdsState);
    blockIds.forEach((blockId) =>
      set(blockDateRangeInputState(blockId), (current) => (current === inputId ? undefined : current)),
    );
  };
